import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  AdminFormPage,
  useAdminFormPersistence,
} from "@components/CatalogIndex";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import { useV2GetCurrencyQuery } from "@generated";
import { CurrencyForm } from "../CurrencyForm/CurrencyForm";
import { useCurrencyMutations } from "../api";
import { emptyCurrencyForm } from "../types";
import type { CurrencyFormValues } from "../types";

const FORM_ID = "currency-form";

export const Edit = (): JSX.Element => {
  const navigate = useNavigate();
  const { id = "" } = useParams();
  const { update } = useCurrencyMutations();
  const { data, isLoading, refetch } = useV2GetCurrencyQuery(
    { id: id },
    { refetchOnMountOrArgChange: true }
  );
  const { toast, setToast, savedInitial, formKey, markSaved } =
    useAdminFormPersistence<CurrencyFormValues>();
  const [submitting, setSubmitting] = useState(false);

  const loadedInitial = useMemo<CurrencyFormValues>(() => {
    const attrs = data?.data?.attributes;
    if (!attrs) return emptyCurrencyForm;
    return {
      name: attrs.name ?? "",
      currencyType: attrs["currency-type"] ?? "",
      conversionRate: attrs["conversion-rate"] ?? null,
      default: !!attrs.default,
    };
  }, [data]);

  if (isLoading) return <ScopeStackSpinner />;

  const initialValues = savedInitial ?? loadedInitial;

  const handleSubmit = async (values: CurrencyFormValues) => {
    setSubmitting(true);
    try {
      await update(id, values);
      markSaved(values, `'${values.name}' saved`);
      // The generated API defines no cache tags, so the update can't
      // invalidate the cached GET. Refetch so the cache holds the
      // server-fresh record for the next time Edit is opened.
      refetch();
    } catch (err) {
      console.error("Failed to update currency", err);
      setToast({ variant: "danger", text: "Could not save currency." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <AdminFormPage
        title="Edit Currency"
        formId={FORM_ID}
        onCancel={() => navigate("../..", { relative: "path" })}
        isSubmitting={submitting}
      >
        <CurrencyForm
          key={formKey}
          id={FORM_ID}
          initialValues={initialValues}
          isSubmitting={submitting}
          onSubmit={handleSubmit}
        />
      </AdminFormPage>
      {toast && (
        <ToastAlert
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};

export default Edit;
